/**
 * Shared SVG utilities for gauge renderers.
 * Pure functions with no React/DOM dependencies (except SR_ONLY_STYLE type).
 */
import type { CSSProperties } from "react";

// --- Interfaces ---

export interface GaugeThreshold {
  min: number;
  max: number;
  color: string;
  label?: string;
}

export interface ArcSegment {
  startAngle: number;
  endAngle: number;
  color: string;
  label?: string;
}

// --- Constants ---

export const defaultColors = ["#10b981", "#e5e7eb"];

// Dial-mode SVG geometry constants
export const DIAL_VIEW_WIDTH = 200;
export const DIAL_VIEW_HEIGHT = 100;
export const DIAL_CX = 100;
export const DIAL_CY = 90;
export const DIAL_OUTER_R = 80;
export const DIAL_INNER_R = 58;

// Unprefixed aliases for backward compatibility within the dispatcher
export const VIEW_WIDTH = DIAL_VIEW_WIDTH;
export const VIEW_HEIGHT = DIAL_VIEW_HEIGHT;
export const CX = DIAL_CX;
export const CY = DIAL_CY;
export const OUTER_R = DIAL_OUTER_R;
export const INNER_R = DIAL_INNER_R;

// Ring-mode SVG geometry constants
export const RING_VIEW_WIDTH = 200;
export const RING_VIEW_HEIGHT = 200;
export const RING_CX = 100;
export const RING_CY = 100;
export const RING_OUTER_R = 80;
export const RING_DEFAULT_THICKNESS = 12;
export const RING_DEFAULT_GAP = 8;

// Bar-mode SVG geometry constants
export const BAR_VIEW_WIDTH = 200;
export const BAR_VIEW_HEIGHT = 40;
export const BAR_PADDING_X = 10;
export const BAR_RIGHT_VALUE_WIDTH = 140;
export const BAR_FULL_WIDTH = 180;
export const BAR_Y_DEFAULT = 8;
export const BAR_DEFAULT_HEIGHT = 24;
export const BAR_DEFAULT_CORNER_RADIUS = 12;

// Milestone-mode SVG geometry constants
export const MILESTONE_VIEW_WIDTH = 200;
export const MILESTONE_VIEW_HEIGHT = 60;
export const MILESTONE_TRACK_Y = 20;
export const MILESTONE_TRACK_X_START = 20;
export const MILESTONE_TRACK_X_END = 180;
export const MILESTONE_TRACK_WIDTH = 160; // TRACK_X_END - TRACK_X_START
export const MILESTONE_TRACK_STROKE = 3;
export const MILESTONE_DEFAULT_MARKER_SIZE = 16;
export const MILESTONE_LABEL_Y = 42;
export const MILESTONE_SUMMARY_Y = 55;
export const MILESTONE_MAX_RENDERED = 20;
export const MILESTONE_LABEL_ROTATE_THRESHOLD = 25; // rotate labels when spacing < this

// --- Milestone Interfaces ---

export interface MilestoneItem {
  id: string;
  label: string;
  completed: boolean;
}

// --- SVG Helper Functions ---

/** Convert polar coordinates to SVG cartesian (y-axis inverted for semicircle top) */
export function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number): { x: number; y: number } {
  const rad = (angleDeg * Math.PI) / 180;
  return {
    x: cx + r * Math.cos(rad),
    y: cy - r * Math.sin(rad),
  };
}

/**
 * Build SVG Path `d` for an annular (ring) arc segment.
 * Outer arc forward, inner arc backward, close path.
 * Angles: 180 = left, 0 = right (standard math convention, y-up)
 */
export function describeAnnularArc(
  cx: number, cy: number, innerR: number, outerR: number,
  startAngle: number, endAngle: number,
): string {
  const sweep = startAngle - endAngle;
  const largeArc = Math.abs(sweep) > 180 ? 1 : 0;

  const outerStart = polarToCartesian(cx, cy, outerR, startAngle);
  const outerEnd = polarToCartesian(cx, cy, outerR, endAngle);
  const innerStart = polarToCartesian(cx, cy, innerR, endAngle);
  const innerEnd = polarToCartesian(cx, cy, innerR, startAngle);

  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${outerR} ${outerR} 0 ${largeArc} 1 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerStart.x} ${innerStart.y}`,
    `A ${innerR} ${innerR} 0 ${largeArc} 0 ${innerEnd.x} ${innerEnd.y}`,
    'Z',
  ].join(' ');
}

/**
 * Build SVG path for an annular arc with optional corner rounding.
 * Uses quarter-circle arcs at each corner (like border-radius) instead of
 * semicircular end caps, so rounding stays within segment bounds.
 */
export function describeRoundedAnnularArc(
  cx: number, cy: number, innerR: number, outerR: number,
  startAngle: number, endAngle: number,
  roundStart: boolean, roundEnd: boolean,
  cornerRadius?: number,
): string {
  const cr = cornerRadius != null ? Math.min(cornerRadius, (outerR - innerR) / 2) : 0;

  // Fall back to flat annular arc if no rounding
  if (cr <= 0 || (!roundStart && !roundEnd)) {
    return describeAnnularArc(cx, cy, innerR, outerR, startAngle, endAngle);
  }

  // Angular offset (degrees) that cr pixels subtend at each radius
  const dOuter = (cr / outerR) * (180 / Math.PI);
  const dInner = (cr / innerR) * (180 / Math.PI);

  // 8 transition points around the segment (4 corners × 2 points each)
  // Path order: outer-arc → end-edge → inner-arc → start-edge

  // After corner 4 (outer-start) / start of outer arc
  const pA = polarToCartesian(cx, cy, outerR, roundStart ? startAngle - dOuter : startAngle);
  // Before corner 1 (outer-end) / end of outer arc
  const pB = polarToCartesian(cx, cy, outerR, roundEnd ? endAngle + dOuter : endAngle);
  // After corner 1 / top of end edge
  const pC = polarToCartesian(cx, cy, roundEnd ? outerR - cr : outerR, endAngle);
  // Before corner 2 / bottom of end edge
  const pD = polarToCartesian(cx, cy, roundEnd ? innerR + cr : innerR, endAngle);
  // After corner 2 / start of inner arc (end side)
  const pE = polarToCartesian(cx, cy, innerR, roundEnd ? endAngle + dInner : endAngle);
  // Before corner 3 / end of inner arc (start side)
  const pF = polarToCartesian(cx, cy, innerR, roundStart ? startAngle - dInner : startAngle);
  // After corner 3 / bottom of start edge
  const pG = polarToCartesian(cx, cy, roundStart ? innerR + cr : innerR, startAngle);
  // Before corner 4 / top of start edge
  const pH = polarToCartesian(cx, cy, roundStart ? outerR - cr : outerR, startAngle);

  // Large-arc flags for the shortened arcs
  const outerSpan = (roundStart ? startAngle - dOuter : startAngle) - (roundEnd ? endAngle + dOuter : endAngle);
  const outerLarge = Math.abs(outerSpan) > 180 ? 1 : 0;
  const innerSpan = (roundStart ? startAngle - dInner : startAngle) - (roundEnd ? endAngle + dInner : endAngle);
  const innerLarge = Math.abs(innerSpan) > 180 ? 1 : 0;

  const parts: string[] = [];

  parts.push(`M ${pA.x} ${pA.y}`);
  // Outer arc
  parts.push(`A ${outerR} ${outerR} 0 ${outerLarge} 1 ${pB.x} ${pB.y}`);
  // Corner 1 (outer-end)
  if (roundEnd) parts.push(`A ${cr} ${cr} 0 0 1 ${pC.x} ${pC.y}`);
  // End edge
  parts.push(`L ${pD.x} ${pD.y}`);
  // Corner 2 (inner-end)
  if (roundEnd) parts.push(`A ${cr} ${cr} 0 0 1 ${pE.x} ${pE.y}`);
  // Inner arc (reverse)
  parts.push(`A ${innerR} ${innerR} 0 ${innerLarge} 0 ${pF.x} ${pF.y}`);
  // Corner 3 (inner-start)
  if (roundStart) parts.push(`A ${cr} ${cr} 0 0 1 ${pG.x} ${pG.y}`);
  // Start edge
  parts.push(`L ${pH.x} ${pH.y}`);
  // Corner 4 (outer-start)
  if (roundStart) parts.push(`A ${cr} ${cr} 0 0 1 ${pA.x} ${pA.y}`);

  parts.push('Z');
  return parts.join(' ');
}

/** Map value to angle: 180 = min (left), 0 = max (right) */
export function valueToAngle(value: number, min: number, max: number): number {
  const range = max - min;
  if (range <= 0) return 90;
  const clamped = Math.min(Math.max(value, min), max);
  return 180 * (1 - (clamped - min) / range);
}

/**
 * Map value to angle for ring (full 360° circle).
 * Start at top (90° in SVG coords), sweep clockwise (decreasing angle).
 * Returns: startAngle at min, startAngle - 360 at max.
 */
export function valueToRingAngle(value: number, min: number, max: number, startAngle = 90): number {
  const range = max - min;
  if (range <= 0) return startAngle;
  const clamped = Math.min(Math.max(value, min), max);
  const fraction = (clamped - min) / range;
  return startAngle - fraction * 360;
}

/**
 * Build threshold segments sorted and gap-filled, returning angle-based segments.
 * `gapDegrees` shrinks each threshold segment by half the gap on each side,
 * creating visible spacing between adjacent segments.
 */
export function buildThresholdSegments(
  thresholds: GaugeThreshold[], min: number, max: number, gapColor: string,
  gapDegrees = 0, arcStart = 180, arcEnd = 0,
): ArcSegment[] {
  if (thresholds.length === 0) return [];

  const sorted = [...thresholds].sort((a, b) => a.min - b.min);
  const segments: ArcSegment[] = [];
  const range = max - min;
  if (range <= 0) return [];

  const halfGap = gapDegrees / 2;

  let cursor = min;

  for (const t of sorted) {
    const tMin = Math.max(t.min, min);
    const tMax = Math.min(t.max, max);
    if (tMax <= tMin) continue;

    if (tMin > cursor) {
      segments.push({
        startAngle: valueToAngle(cursor, min, max),
        endAngle: valueToAngle(tMin, min, max),
        color: gapColor,
      });
    }

    let startAngle = valueToAngle(tMin, min, max);
    let endAngle = valueToAngle(tMax, min, max);

    // Apply gap: shrink segment inward (startAngle decreases, endAngle increases)
    if (gapDegrees > 0) {
      startAngle -= halfGap;
      endAngle += halfGap;
      // Don't let gap collapse the segment
      if (startAngle <= endAngle) {
        const mid = (valueToAngle(tMin, min, max) + valueToAngle(tMax, min, max)) / 2;
        startAngle = mid + 0.5;
        endAngle = mid - 0.5;
      }
    }

    segments.push({ startAngle, endAngle, color: t.color, label: t.label || `${t.min}\u2013${t.max}` });

    cursor = tMax;
  }

  if (cursor < max) {
    segments.push({
      startAngle: valueToAngle(cursor, min, max),
      endAngle: valueToAngle(max, min, max),
      color: gapColor,
    });
  }

  // Clamp all angles to the inset arc range
  for (const seg of segments) {
    seg.startAngle = Math.min(seg.startAngle, arcStart);
    seg.endAngle = Math.max(seg.endAngle, arcEnd);
  }

  return segments;
}

/** Compute two triangle paths (tips pointing toward each other) spanning the arc band */
export function getNeedleTriangles(angle: number, cx: number, cy: number, innerR: number, outerR: number) {
  const rad = (angle * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const midR = (innerR + outerR) / 2;
  const baseHalf = 5; // half-width of triangle base

  // Perpendicular direction in SVG space
  const px = sin;
  const py = cos;

  // Midpoint where tips meet
  const midX = cx + midR * cos;
  const midY = cy - midR * sin;

  // Outer triangle: base at outer edge, tip pointing inward
  const outerR2 = outerR + 2;
  const obx = cx + outerR2 * cos;
  const oby = cy - outerR2 * sin;
  const outerPath = `M${midX},${midY}L${obx + baseHalf * px},${oby + baseHalf * py} ${obx - baseHalf * px},${oby - baseHalf * py}Z`;

  // Inner triangle: base at inner edge, tip pointing outward
  const innerR2 = innerR - 2;
  const ibx = cx + innerR2 * cos;
  const iby = cy - innerR2 * sin;
  const innerPath = `M${midX},${midY}L${ibx + baseHalf * px},${iby + baseHalf * py} ${ibx - baseHalf * px},${iby - baseHalf * py}Z`;

  return { outerPath, innerPath };
}

/** Find which threshold zone the current value falls into */
export function getValueColorFromThresholds(
  value: number,
  thresholds: GaugeThreshold[],
  fallbackColor: string
): string {
  for (const threshold of thresholds) {
    if (value >= threshold.min && value <= threshold.max) {
      return threshold.color;
    }
  }
  return fallbackColor;
}

// --- New utilities for future renderers (Phase 2-4) ---

/** Replace {value} and {max} placeholders in a template string */
export function formatGaugeValue(template: string, value: number, max: number): string {
  return template
    .replace(/\{value\}/g, String(value))
    .replace(/\{max\}/g, String(max));
}

/** Map a gauge value to a linear pixel position (for bar/milestone renderers) */
export function valueToLinearPosition(value: number, min: number, max: number, barWidth: number): number {
  const range = max - min;
  if (range <= 0) return 0;
  const clamped = Math.min(Math.max(value, min), max);
  return ((clamped - min) / range) * barWidth;
}

/** Compute SVG X positions for milestone markers. Handles 0, 1, 2, and N-item distributions. */
export function getMilestoneMarkerPositions(total: number): number[] {
  if (total === 0) return [];
  if (total === 1) return [MILESTONE_TRACK_X_START + MILESTONE_TRACK_WIDTH / 2];
  if (total === 2) return [MILESTONE_TRACK_X_START, MILESTONE_TRACK_X_END];
  return Array.from({ length: total }, (_, i) =>
    MILESTONE_TRACK_X_START + (i / (total - 1)) * MILESTONE_TRACK_WIDTH
  );
}

// --- Accessibility Utilities ---

/** Visually hidden but screen-reader-accessible style. */
export const SR_ONLY_STYLE: CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  borderWidth: 0,
};

/**
 * Returns a text color that achieves 4.5:1 contrast ratio against the
 * given background color per WCAG 2.1 AA.
 *
 * @param backgroundColor - Hex color string (#RRGGBB or #RGB)
 * @returns '#ffffff' for dark backgrounds, '#1f2937' for light backgrounds
 */
export function getContrastTextColor(backgroundColor: string): string {
  const hex = backgroundColor.replace('#', '');
  let r: number, g: number, b: number;

  if (hex.length === 3) {
    r = parseInt(hex[0] + hex[0], 16) / 255;
    g = parseInt(hex[1] + hex[1], 16) / 255;
    b = parseInt(hex[2] + hex[2], 16) / 255;
  } else {
    r = parseInt(hex.substring(0, 2), 16) / 255;
    g = parseInt(hex.substring(2, 4), 16) / 255;
    b = parseInt(hex.substring(4, 6), 16) / 255;
  }

  // W3C relative luminance formula
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance < 0.179 ? '#ffffff' : '#1f2937';
}
